import { motion } from 'framer-motion';
import { Zap, Wrench, Sun, Building2, Home, Settings, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';

const Services = () => {
  const mainServices = [
    {
      icon: Zap,
      title: 'Commercial Electrical Design',
      description: 'Comprehensive electrical system design for commercial properties, including power distribution, lighting systems, and specialized equipment requirements.',
      features: [
        'Load analysis and capacity planning',
        'Power distribution system design',
        'Lighting design and control systems',
        'Energy efficiency optimization',
        'Code compliance documentation',
      ],
    },
    {
      icon: Wrench,
      title: 'Electrical Installation',
      description: 'Professional installation of electrical systems with focus on safety, efficiency, and compliance with all industry standards.',
      features: [
        'Main service panel installation',
        'Branch circuit wiring',
        'Lighting fixture installation',
        'Grounding and bonding systems',
        'Final testing and inspection',
      ],
    },
    {
      icon: Sun,
      title: 'Solar Power Solutions',
      description: 'Complete solar power system design and installation for sustainable energy solutions and reduced operational costs.',
      features: [
        'Solar panel system design',
        'Inverter and battery storage',
        'Grid-tied and off-grid systems',
        'Energy monitoring systems',
        'Maintenance and support',
      ],
    },
    {
      icon: Building2,
      title: 'Neighborhood Infrastructure',
      description: 'Large-scale electrical infrastructure for entire neighborhood developments and community projects.',
      features: [
        'Underground utility installation',
        'Street lighting systems',
        'Community power distribution',
        'Backup power systems',
        'Smart grid integration',
      ],
    },
  ];

  const additionalServices = [
    {
      icon: Home,
      title: 'Luxury Residential',
      description: 'High-end electrical solutions for multi-million dollar homes with advanced automation and premium finishes.',
    },
    {
      icon: Settings,
      title: 'Maintenance Services',
      description: 'Ongoing maintenance and support for electrical systems to ensure optimal performance and safety.',
    },
  ];

  return (
    <div>
      {/* Hero */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Our Services</motion.p>
          <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-4 max-w-3xl">
            Our Services
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            Comprehensive electrical solutions for commercial properties, luxury residences, and large-scale developments.
          </motion.p>
        </div>
      </motion.section>

      {/* Main Services */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-900"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-navy-600">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="bg-navy-900 p-10"
              >
                <service.icon className="h-6 w-6 text-primary-500 mb-6" />
                <h3 className="font-heading text-h3 text-white uppercase">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed mt-4">{service.description}</p>
                <div className="space-y-3 mt-6">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-400 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="btn-primary inline-flex items-center mt-8">
                  Get Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Additional Services */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Additional Services</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
            Additional Services
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            Complementary services to support your electrical project needs.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mt-16">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="border-t border-navy-600 pt-8 pb-8 pr-8"
              >
                <service.icon className="h-6 w-6 text-primary-500 mb-6" />
                <h3 className="font-heading text-h3 text-white uppercase">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed mt-4">{service.description}</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-primary-500 hover:text-primary-400 font-heading uppercase text-sm tracking-wide mt-6 transition-colors"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Process */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-900"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Our Process</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
            Our Process
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            A systematic approach to ensure successful project delivery from concept to completion.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 mt-16">
            {[
              { step: '01', title: 'Consultation', description: 'Initial project assessment and requirements gathering' },
              { step: '02', title: 'Design',        description: 'Detailed electrical system design and planning' },
              { step: '03', title: 'Installation',  description: 'Professional installation with quality assurance' },
              { step: '04', title: 'Completion',    description: 'Final inspection, testing, and handover' },
            ].map((process, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="border-t border-navy-600 pt-8 pb-8 pr-8"
              >
                <div className="font-heading text-h2 text-primary-500 leading-none">{process.step}</div>
                <h3 className="font-heading text-white uppercase text-xl mt-4">{process.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-3">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Get Started</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4 max-w-3xl">
            Ready to Start Your Project?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            Contact our expert team today to discuss your electrical project requirements and get a customized solution.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10">
            <Link to="/contact" className="btn-primary inline-flex items-center">
              Get Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Services;
